import { FlexSpacer } from 'ui/flexSpacer';
import { colors, spacing } from 'ui/theme';
import { localizePercent } from 'utils/l10n/percent';
import { Delta, StyledCaption, Value } from './MetricsDetails.styled';
import { DeltaType, getMetricDeltaInfo } from './MetricsDetails.utils';
import { TrendUp24 } from 'ui/icons/aurora/24px/TrendUp24';
import { TrendDown24 } from 'ui/icons/aurora/24px/TrendDown24';

type MetricsDetailsProps = {
  /**
   * The delta of the metric.
   * @example 0.1
   * @example -0.1
   */
  delta?: number;
  /**
   * The value of the metric.
   * @example 100
   */
  metricValue?: number;
  /**
   * The name of the metric.
   * @example "Open Rate"
   * @example "Total Bounces"
   * @example "Total Sent"
   * @example "Delivery Rate"
   * @example "Total Opens"
   */
  metricName?: string;
  /**
   * The time period of the metric.
   * @example "from previous period"
   * @example "in last 7 days"
   * @example "in last 30 days"
   */
  timePeriod: string;
};

export const METRICS_DETAILS_TEST_IDS = {
  UP_TREND: 'up-trend',
  DOWN_TREND: 'down-trend',
  DELTA: 'delta',
};

export const MetricsDetails = ({
  delta,
  metricName,
  metricValue,
  timePeriod = 'from previous period',
}: MetricsDetailsProps) => {
  const value = metricValue || 0;

  const { isPositiveTrend, isNegativeTrend, deltaType } = getMetricDeltaInfo(
    delta || 0,
    metricName,
  );

  const deltaColor = deltaType === DeltaType.Positive ? colors.green100 : colors.textWarning;
  const deltaBackgroundColor = deltaType === DeltaType.Positive ? colors.green10 : colors.mustard10;

  return (
    <FlexSpacer gap={spacing.half}>
      <Value>{value.toLocaleString()}</Value>
      {!!value && delta !== undefined && (
        <>
          {isPositiveTrend && (
            <TrendUp24
              color={deltaColor}
              backgroundColor={deltaBackgroundColor}
              data-test={METRICS_DETAILS_TEST_IDS.UP_TREND}
            />
          )}
          {isNegativeTrend && (
            <TrendDown24
              color={deltaColor}
              backgroundColor={deltaBackgroundColor}
              data-test={METRICS_DETAILS_TEST_IDS.DOWN_TREND}
            />
          )}
          <Delta data-delta-type={deltaType} data-test={METRICS_DETAILS_TEST_IDS.DELTA}>
            {localizePercent(delta, undefined, undefined, { signDisplay: 'exceptZero' })}
          </Delta>
          <StyledCaption>{timePeriod}</StyledCaption>
        </>
      )}
    </FlexSpacer>
  );
};
